import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Home, Users, CreditCard, Settings, LogOut, Building, ShieldCheck, FileText, Bell } from 'lucide-react';
import { Button } from './ui/Button';
interface DashboardLayoutProps {
  children: ReactNode;
  userType: 'student' | 'landlord' | 'university';
}
export function DashboardLayout({
  children,
  userType
}: DashboardLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const handleLogout = () => {
    navigate('/login');
  };
  const studentLinks = [{
    href: '/dashboard/student',
    label: 'Overview',
    icon: LayoutDashboard
  }, {
    href: '/find-hostel',
    label: 'Find Hostel',
    icon: Home
  }, {
    href: '/roommate-match',
    label: 'Roommates',
    icon: Users
  }, {
    href: '/dashboard/student/payments',
    label: 'Payments',
    icon: CreditCard
  }];
  const landlordLinks = [{
    href: '/dashboard/landlord',
    label: 'Overview',
    icon: LayoutDashboard
  }, {
    href: '/dashboard/landlord/properties',
    label: 'Properties',
    icon: Building
  }, {
    href: '/dashboard/landlord/tenants',
    label: 'Tenants',
    icon: Users
  }, {
    href: '/dashboard/landlord/payments',
    label: 'Collections',
    icon: CreditCard
  }];
  const universityLinks = [{
    href: '/dashboard/university',
    label: 'Overview',
    icon: LayoutDashboard
  }, {
    href: '/dashboard/university/registry',
    label: 'Student Registry',
    icon: Users
  }, {
    href: '/dashboard/university/safety',
    label: 'Safety Map',
    icon: ShieldCheck
  }, {
    href: '/dashboard/university/reports',
    label: 'Reports',
    icon: FileText
  }];
  const links = {
    student: studentLinks,
    landlord: landlordLinks,
    university: universityLinks
  }[userType];
  return <div className="min-h-screen bg-slate-50 flex" data-id="element-24" data-mp="2v7sv6jwl4wtg">
      {/* Sidebar */}
      <aside className="w-64 bg-brand-dark text-white fixed h-full hidden md:flex flex-col" data-id="element-25" data-mp="2qaaiqjjczzgp">
        <div className="p-6 border-b border-brand-primary/20" data-id="element-26" data-mp="349a67nv4y8ba">
          <Link to="/" className="flex items-center justify-center" data-id="element-27" data-mp="347fwl5pbe2mi">
            <img src="https://cdn.magicpatterns.com/uploads/9MtegeU9Vd3YNSs5ksGQKv/Cam_main_ver_wht_logo.png" alt="CampusAparte" className="h-16 w-auto" data-id="element-28" data-mp="3099uiazf8b7x" />
          </Link>
          <div className="mt-4 px-3 py-1 bg-brand-dark-secondary rounded-full inline-block text-xs font-medium text-slate-400 uppercase tracking-wider" data-id="element-29" data-mp="27l453axf9y1r">
            {userType} Portal
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1" data-id="element-30" data-mp="2gi4spfco3s45">
          {links.map(link => {
          const Icon = link.icon;
          const isActive = location.pathname === link.href;
          return <Link key={link.href} to={link.href} className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${isActive ? 'bg-brand-primary text-white font-medium' : 'text-slate-400 hover:bg-brand-dark-secondary hover:text-white'}`} data-id="element-31" data-mp="1oqyxndbt0klq">
                <Icon className="w-5 h-5" data-id="element-32" data-mp="19pieuhwrugv1" />
                {link.label}
              </Link>;
        })}
        </nav>

        <div className="p-4 border-t border-brand-primary/20" data-id="element-33" data-mp="1xge1l0mdelvf">
          <button onClick={handleLogout} className="flex items-center gap-3 px-4 py-3 w-full text-slate-400 hover:text-white hover:bg-brand-dark-secondary rounded-lg transition-colors" data-id="element-34" data-mp="22efndtb6kzal">
            <LogOut className="w-5 h-5" data-id="element-35" data-mp="1sfglquou23x3" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 md:ml-64" data-id="element-36" data-mp="c0gggpvkdrcl">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 h-16 flex items-center justify-between px-8 sticky top-0 z-30" data-id="element-37" data-mp="h1cs5pm5qhsk">
          <h1 className="text-xl font-semibold text-slate-800 capitalize" data-id="element-38" data-mp="x9seiumeb5vj">
            {links.find(l => l.href === location.pathname)?.label || 'Dashboard'}
          </h1>
          <div className="flex items-center gap-4" data-id="element-39" data-mp="njh72y3i6ejw">
            <button className="p-2 text-slate-500 hover:bg-slate-100 rounded-full relative" data-id="element-40" data-mp="ijctbftzbxxd">
              <Bell className="w-5 h-5" data-id="element-41" data-mp="ihi8s2mme7tt" />
              <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full" data-id="element-42" data-mp="1g620s57gb2qm"></span>
            </button>
            <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center text-slate-600 font-bold" data-id="element-43" data-mp="1b9blhk3h0b69">
              {userType.charAt(0).toUpperCase()}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-8" data-id="element-44" data-mp="3qz4vjvnnk1ic">{children}</main>
      </div>
    </div>;
}